/**
 * EditableSection Component
 * 
 * Wraps page sections to make them selectable in edit mode.
 * Provides visual feedback (hover outlines, selection state)
 * and handles click-to-select functionality.
 */

import {
  createContext,
  useContext,
  useRef,
  useCallback,
  useEffect,
  useState,
  useMemo,
  type CSSProperties,
  type ReactNode,
  type MouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import type { ComponentSlot } from '../types';
import type { ComponentRequirements } from '@vibesites/tokens';
import { useEditMode, postScopeTargetSelected, postCommentPinTargetSelected } from './edit-mode';
import type { ComponentEditInfo } from './edit-mode';
import { useScopedStyleOverride } from './context';

// ============================================================================
// Section Edit Info Context
// ============================================================================

/**
 * Context providing the parent section's ComponentEditInfo to child elements
 * (tokens, slot groups). Used in sidebar mode so child clicks can also select
 * the parent component, ensuring the ComponentEditorSidebar has full context.
 */
const SectionEditInfoContext = createContext<ComponentEditInfo | null>(null);

/** Hook for child elements to access their parent section's edit info. */
export function useSectionEditInfo(): ComponentEditInfo | null {
  return useContext(SectionEditInfoContext);
}

// ============================================================================
// Types
// ============================================================================

export interface EditableSectionProps {
  /** Component slot name */
  slot: ComponentSlot;
  /** Section ID from page composition */
  sectionId: string;
  /** Props passed to the component */
  sectionProps?: Record<string, unknown>;
  /** Component requirements (design tokens, data tokens) */
  requirements?: ComponentRequirements;
  /** Children to render */
  children: ReactNode;
  /** Additional class name */
  className?: string;
}

/** Shared style for the hover toolbar's Edit button. */
const TOOLBAR_BUTTON_STYLE: CSSProperties = {
  background: '#1f2937',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  transition: 'background-color 0.15s',
};

const handleToolbarButtonMouseEnter = (e: MouseEvent<HTMLButtonElement>) => {
  e.currentTarget.style.backgroundColor = '#374151';
};

const handleToolbarButtonMouseLeave = (e: MouseEvent<HTMLButtonElement>) => {
  e.currentTarget.style.backgroundColor = '#1f2937';
};

// ============================================================================
// Component
// ============================================================================

/**
 * EditableSection wraps a component section to make it selectable in edit mode.
 * 
 * Features:
 * - Shows outline on hover when edit mode is active
 * - Shows slot name label on hover
 * - Handles click to select component
 * - Sends component info to parent for the editor sidebar
 */
export function EditableSection({
  slot,
  sectionId,
  sectionProps,
  requirements,
  children,
  className = '',
}: EditableSectionProps) {
  const editMode = useEditMode();
  const sectionRef = useRef<HTMLDivElement>(null);
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null); // delay before dismissing hover so moving to portaled Edit button doesn't flicker
  const [isHovered, setIsHovered] = useState(false);
  const styleOverride = useScopedStyleOverride(`_section.${slot}`);

  // Get edit mode state (gracefully handle when not in edit mode context)
  const isEditMode = editMode?.isEditMode ?? false;
  const selectedComponent = editMode?.selectedComponent;
  const isSelected = selectedComponent?.slot === slot && selectedComponent?.sectionId === sectionId;

  // Handle mouse enter
  const handleMouseEnter = useCallback(() => {
    if (!isEditMode) return;
    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }
    setIsHovered(true);
    editMode?.setHoveredSlot(slot);
  }, [isEditMode, editMode, slot]);

  // Handle mouse leave — short delay so moving to the portaled Edit button doesn't flicker
  const handleMouseLeave = useCallback(() => {
    if (!isEditMode) return;
    leaveTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
      editMode?.setHoveredSlot(null);
      leaveTimeoutRef.current = null;
    }, 10);
  }, [isEditMode, editMode]);

  // Dismiss hover portal on scroll so fixed-position labels don't drift
  useEffect(() => {
    if (!isEditMode || !isHovered) return;
    const dismiss = () => {
      if (leaveTimeoutRef.current) {
        clearTimeout(leaveTimeoutRef.current);
        leaveTimeoutRef.current = null;
      }
      setIsHovered(false);
      editMode?.setHoveredSlot(null);
    };
    window.addEventListener('scroll', dismiss, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', dismiss, { capture: true });
  }, [isEditMode, isHovered, editMode]);

  // Clean up pending timeouts on unmount
  useEffect(() => {
    return () => {
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
    };
  }, []);

  // Handle click to select
  const handleClick = useCallback(() => {
    if (!isEditMode || !editMode) return;

    // Get bounding rect for positioning
    const rect = sectionRef.current?.getBoundingClientRect();
    const bounds = rect ? {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    } : undefined;

    const componentInfo: ComponentEditInfo = {
      slot,
      sectionId,
      sectionProps,
      requirements,
      bounds,
    };

    // Scope mode routes the click to the parent's AI composer instead of
    // selecting the component for the editor sidebar.
    if (editMode.scopeMode) {
      postScopeTargetSelected({ kind: 'section', componentInfo });
      return;
    }
    if (editMode.commentPinMode) {
      postCommentPinTargetSelected({ kind: 'section', componentInfo });
      return;
    }

    editMode.selectComponent(componentInfo);
  }, [isEditMode, editMode, slot, sectionId, sectionProps, requirements]);

  const handleClickCapture = useCallback((e: MouseEvent) => {
    if (!isEditMode || !editMode) return;
    const target = e.target as HTMLElement;
    const nestedEditable = target.closest(
      '[data-vibesite-token], [data-vibesite-slot-group], [data-vibesite-collection-binding]',
    );
    if (nestedEditable && sectionRef.current?.contains(nestedEditable) && nestedEditable !== sectionRef.current) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    (e.nativeEvent as Event & { stopImmediatePropagation?: () => void }).stopImmediatePropagation?.();
    handleClick();
  }, [isEditMode, editMode, handleClick]);

  // Handle edit button click
  const handleEditClick = useCallback((e: MouseEvent) => {
    e.stopPropagation();
    handleClick();
  }, [handleClick]);

  // Memoized edit info for the section context — consumed by child tokens/slot groups
  // so they can also select this component in sidebar mode
  const sectionEditInfo = useMemo<ComponentEditInfo>(() => ({
    slot,
    sectionId,
    sectionProps,
    requirements,
  }), [slot, sectionId, sectionProps, requirements]);

  // If not in edit mode context, just render children (wrap if style overrides exist)
  if (!editMode) {
    if (styleOverride) {
      return <div style={styleOverride.properties}>{children}</div>;
    }
    return <>{children}</>;
  }

  // Compute fixed-position coords for portaled overlays from the element's rect
  const rect = (isEditMode && isHovered) ? sectionRef.current?.getBoundingClientRect() : null;

  return (
    <>
      <div
        ref={sectionRef}
        className={`vibesite-editable-section ${className}`}
        data-vibesite-slot={slot}
        data-vibesite-section-id={sectionId}
        data-vibesite-edit-mode={isEditMode}
        data-vibesite-hovered={isHovered}
        data-vibesite-selected={isSelected}
        data-vibesite-comment-pin={editMode.commentPinMode}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClickCapture={handleClickCapture}
        style={{
          cursor: isEditMode ? 'pointer' : undefined,
          ...styleOverride?.properties,
        }}
      >
        <SectionEditInfoContext.Provider value={sectionEditInfo}>
          {children}
        </SectionEditInfoContext.Provider>

        {/* Selection/hover outline styles — uses outline (no position: relative needed) */}
        <style>{`
          .vibesite-editable-section[data-vibesite-edit-mode="true"][data-vibesite-hovered="true"] {
            outline: 2px dashed #3b82f6 !important;
            outline-offset: 4px !important;
          }

          .vibesite-editable-section[data-vibesite-edit-mode="true"][data-vibesite-hovered="true"][data-vibesite-comment-pin="true"] {
            outline: 2px dashed #14b8a6 !important;
            outline-offset: 4px !important;
          }
          
          .vibesite-editable-section[data-vibesite-edit-mode="true"][data-vibesite-selected="true"] {
            outline: 3px solid #773BFF !important;
            outline-offset: 4px !important;
            animation: vibesite-pulse 2s ease-in-out infinite;
          }
          
          @keyframes vibesite-pulse {
            0%, 100% { outline-color: #773BFF; }
            50% { outline-color: #9466FF; }
          }
        `}</style>
      </div>

      {/* Portaled slot label — rendered outside the section to avoid position/style interference */}
      {isEditMode && isHovered && rect && createPortal(
        <div
          className="vibesite-slot-label"
          style={{
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            transform: 'translateY(-100%)',
            background: '#1f2937',
            color: '#fff',
            fontSize: '11px',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            padding: '3px 8px',
            borderRadius: '4px 4px 0 0',
            zIndex: 10000,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {slot}
        </div>,
        document.body,
      )}

      {/* Portaled hover toolbar (Edit button) */}
      {isEditMode && isHovered && rect && createPortal(
        <div
          className="vibesite-edit-toolbar"
          style={{
            position: 'fixed',
            top: rect.top + 8,
            left: rect.left + rect.width - 8,
            transform: 'translateX(-100%)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            zIndex: 10000,
          }}
          onMouseEnter={() => {
            if (leaveTimeoutRef.current) {
              clearTimeout(leaveTimeoutRef.current);
              leaveTimeoutRef.current = null;
            }
          }}
          onMouseLeave={() => {
            leaveTimeoutRef.current = setTimeout(() => {
              setIsHovered(false);
              editMode?.setHoveredSlot(null);
              leaveTimeoutRef.current = null;
            }, 80);
          }}
        >
          <button
            className="vibesite-edit-button"
            onClick={handleEditClick}
            style={TOOLBAR_BUTTON_STYLE}
            onMouseEnter={handleToolbarButtonMouseEnter}
            onMouseLeave={handleToolbarButtonMouseLeave}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Edit
          </button>
        </div>,
        document.body,
      )}
    </>
  );
}

export default EditableSection;

